/**
 * EMA Back-in-Stock Widget
 *
 * The form HTML is already server-rendered by the Liquid snippet,
 * so this script only wires up interactivity:
 *   • Pre-fills the email from localStorage if the customer isn't logged in
 *   • Async-checks subscription status; replaces the form with a
 *     "subscribed" indicator if the visitor is already signed up
 *   • Handles form submit, and "Remove alert" from the subscribed state
 */
(function () {
  'use strict';

  const API_BASE  = 'https://ema-chatbot-api-production.up.railway.app';
  const EMAIL_KEY = 'ema_bis_email';

  // ── Storage helpers ───────────────────────────────────────────────────────────
  function getStoredEmail()  { try { return localStorage.getItem(EMAIL_KEY) || ''; } catch { return ''; } }
  function storeEmail(e)     { try { localStorage.setItem(EMAIL_KEY, e); }  catch {} }
  function clearStoredEmail(){ try { localStorage.removeItem(EMAIL_KEY); }  catch {} }

  // ── API helpers ───────────────────────────────────────────────────────────────
  function apiPost(path, body) {
    return fetch(API_BASE + path, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  function apiGetStatus(email, handle) {
    return fetch(
      API_BASE + '/api/stock-alerts/status'
      + '?email='  + encodeURIComponent(email)
      + '&handle=' + encodeURIComponent(handle)
    ).then(function (r) { return r.json(); });
  }

  // ── Widget init ───────────────────────────────────────────────────────────────
  function initWidget(root) {
    var handle  = root.dataset.handle;
    var title   = root.dataset.title   || '';
    // Customer email injected by Liquid (logged-in); fall back to localStorage
    var custEmail = (root.dataset.customerEmail || '').trim();
    var bestEmail = custEmail || getStoredEmail();

    if (!handle) return;

    // Pre-fill the input if it doesn't already have a value
    var input = root.querySelector('.ema-bis-input');
    if (input && !input.value && bestEmail) {
      input.value = bestEmail;
    }

    // Wire up the submit button
    wireForm(root, handle, title);

    // Async check: if we have an email, find out if they're already subscribed.
    // We do this after wiring the form so the page is interactive immediately.
    if (bestEmail) {
      apiGetStatus(bestEmail, handle)
        .then(function (status) {
          if (status.subscribed && status.active) {
            renderSubscribed(root, handle, bestEmail);
          }
        })
        .catch(function () { /* network error — leave the form visible */ });
    }
  }

  // ── Form interactivity ────────────────────────────────────────────────────────
  function wireForm(root, handle, title) {
    var input = root.querySelector('.ema-bis-input');
    var btn   = root.querySelector('.ema-bis-btn');
    var msg   = root.querySelector('.ema-bis-msg');
    if (!input || !btn) return;

    function submit() {
      var email = input.value.trim();
      if (!email || !isValidEmail(email)) {
        showMsg(msg, 'Please enter a valid email address.', 'error');
        input.focus();
        return;
      }
      btn.disabled    = true;
      btn.textContent = 'Saving…';
      hideMsg(msg);

      apiPost('/api/stock-alerts/subscribe', {
        email:          email,
        product_handle: handle,
        product_title:  title,
        session_id:     window.EMA_SESSION_ID   || undefined,
        customer_id:    window.EMA_CUSTOMER_ID  || undefined,
      }).then(function (data) {
        if (data.ok) {
          storeEmail(email);
          renderSubscribed(root, handle, email);
        } else {
          showMsg(msg, data.error || 'Something went wrong. Please try again.', 'error');
          btn.disabled    = false;
          btn.textContent = 'Alert me';
        }
      }).catch(function () {
        showMsg(msg, 'Network error — please try again.', 'error');
        btn.disabled    = false;
        btn.textContent = 'Alert me';
      });
    }

    btn.addEventListener('click', submit);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') submit(); });
  }

  // ── Subscribed state ──────────────────────────────────────────────────────────
  function renderSubscribed(root, handle, email) {
    root.innerHTML =
      '<div class="ema-bis-active">'
        + '<div class="ema-bis-active-text">'
          + '<span class="ema-bis-active-title">'
            + '<span class="ema-bis-active-dot" aria-hidden="true"></span>'
            + 'Alert active'
          + '</span>'
          + '<span class="ema-bis-active-email">' + escText(email) + '</span>'
        + '</div>'
        + '<button class="ema-bis-remove-btn" type="button">Remove alert</button>'
      + '</div>';

    root.querySelector('.ema-bis-remove-btn').addEventListener('click', function () {
      if (!confirm('Remove your stock alert for this product?')) return;
      apiPost('/api/stock-alerts/unsubscribe', { email: email, product_handle: handle })
        .then(function (data) {
          if (data.ok) {
            clearStoredEmail();
            // Restore the original form (reload the widget area)
            location.reload();
          }
        })
        .catch(function () {});
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────
  function showMsg(el, text, type) {
    if (!el) return;
    el.textContent       = text;
    el.dataset.type      = type;
    el.hidden            = false;
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.hidden = true; }, 5000);
  }
  function hideMsg(el)   { if (el) el.hidden = true; }
  function isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
  function escText(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ── Boot ──────────────────────────────────────────────────────────────────────
  function boot() {
    document.querySelectorAll('.ema-bis-root').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // ── Chatbot integration ───────────────────────────────────────────────────────
  window.EMA_BIS = {
    subscribe: function (email, handle, title) {
      return apiPost('/api/stock-alerts/subscribe', {
        email:          email,
        product_handle: handle,
        product_title:  title || '',
        session_id:     window.EMA_SESSION_ID  || undefined,
        customer_id:    window.EMA_CUSTOMER_ID || undefined,
      }).then(function (data) {
        if (data.ok) storeEmail(email);
        return data;
      });
    },
  };
})();
